import { createMongoAbility, subject, type ForcedSubject, type MongoAbility } from '@casl/ability'
// oxlint-disable-next-line no-restricted-imports
import { Can as CaslCan, useAbility as useCaslAbility, type CanProps } from '@casl/react'
import {
  REAL_ESTATE_RESTRICTED_STATUSES,
  type CurrentAdminRbacPermissionGrant,
  rbacResourceDetails,
  type RbacV2AbilityTuple,
  rbacV2Abilities,
  type RbacV2RolePermission,
  type ResourceAbilityTuple,
} from '@gozoki/api-types'
import type { ReactNode } from 'react'

// Real-estate grants are scoped by project status, so they are checked with object subjects
// (`subject('real-estate', { status })`) instead of the bare string subject. Allow that object
// form alongside the string-subject tuples derived from the RBAC registry.
type RealEstateSubject = { status: string } & ForcedSubject<'real-estate'>

/**
 * Tuple `[action, resource]` acceptable partout où le front vérifie une permission
 * (route `staticData.permission`, `RequireResourcePermission`, `Can`, `ability.can(...)`).
 *
 * Union des deux taxonomies pour que les mêmes hooks/composants gardent l'UI aussi bien sur
 * les permissions v1 (`rbac.ts`) que v2 (`rbac.v2.ts`, flag `permission-v2`).
 */
export type PermissionTuple = ResourceAbilityTuple | RbacV2AbilityTuple

export type AppAbility = MongoAbility<PermissionTuple | ['read' | 'edit', RealEstateSubject]>

export { subject }

const toRule = (grant: CurrentAdminRbacPermissionGrant) => {
  const base = { action: grant.permissionType, subject: grant.resource }
  if (grant.scope === 'global') return base
  if (grant.resource === 'real-estate' && grant.scope === 'real-estate-restricted') {
    return { ...base, conditions: { status: { $in: REAL_ESTATE_RESTRICTED_STATUSES } } }
  }
  return { ...base, conditions: { scope: grant.scope } }
}

// La v2 n'a plus de scope : chaque grant devient une règle plate `[action, resource]`.
const toRuleV2 = (grant: RbacV2RolePermission) => ({
  action: grant.action,
  subject: grant.resource,
})

// Quand l'enforcement d'une version est désactivé, on n'utilise PAS le mot-clé spécial `manage`
// de CASL : on autorise explicitement chaque couple (action, ressource) déclaré dans la taxonomie,
// si bien que tout check *valide* passe tout en restant borné aux actions réellement définies.
const v1AllowAllRules = () =>
  Object.entries(rbacResourceDetails).flatMap(([resource, detail]) =>
    detail.actions.map((action) => ({ action, subject: resource })),
  )

const v2AllowAllRules = () =>
  Object.entries(rbacV2Abilities).flatMap(([resource, actions]) =>
    actions.map((action) => ({ action, subject: resource })),
  )

/**
 * Règles injectées dans l'ability selon les flags de l'admin courant. La décision est portée par
 * l'ability elle-même : aucun site d'appel (`RequireResourcePermission`, `Can`,
 * `withRoutePermissions`, `AuthorizedButtonLink`, hooks…) n'a à reconsulter les flags.
 * - `enforcementEnabled` OFF → toutes les abac v1 passent (autorisation explicite de la taxonomie) ;
 * - `permissionV2Enabled` OFF → toutes les abac v2 passent (idem) ; ON → grants v2 réels enforced.
 */
const v1Rules = (grants: CurrentAdminRbacPermissionGrant[], enforcementEnabled: boolean) =>
  enforcementEnabled ? grants.map(toRule) : v1AllowAllRules()

const v2Rules = (grantsV2: RbacV2RolePermission[], permissionV2Enabled: boolean) =>
  permissionV2Enabled ? grantsV2.map(toRuleV2) : v2AllowAllRules()

interface CreateAppAbilityFlags {
  /** Master switch RBAC v1 (`ENFORCE_RBAC_SYSTEM`). Défaut `true` = v1 enforced. */
  enforcementEnabled?: boolean
  /** Flag Flipt `permission-v2` de l'admin. Défaut `false` = v2 inerte. */
  permissionV2Enabled?: boolean
}

export const createAppAbility = (
  grants: CurrentAdminRbacPermissionGrant[],
  grantsV2: RbacV2RolePermission[] = [],
  { enforcementEnabled = true, permissionV2Enabled = false }: CreateAppAbilityFlags = {},
): AppAbility =>
  createMongoAbility([
    // Les deux taxonomies ne se cumulent jamais : `permission-v2` ON ⇒ la v1 n'existe plus pour
    // cet admin, ses grants v2 décident seuls.
    ...v1Rules(grants, enforcementEnabled && !permissionV2Enabled),
    ...v2Rules(grantsV2, permissionV2Enabled),
  ]) as unknown as AppAbility

export const useAbility = () => useCaslAbility<AppAbility>()

export const Can: (props: CanProps<AppAbility>) => ReactNode = CaslCan
